import { useState } from "react";
import { toast } from "sonner";
import { readJson, uploadMedia } from "./adminApi";

const EMPTY_GUIDE = { name: "", profession: "", place: "", batch: "", phone: "", bio: "", photo_position_x: 50, photo_position_y: 50, photo_zoom: 1 };

const guideForm = (g) => ({
  name: g.name || "", profession: g.profession || "", place: g.place || "", batch: g.batch || "", phone: g.phone || "", bio: g.bio || "",
  photo_position_x: g.photo_position_x ?? 50, photo_position_y: g.photo_position_y ?? 50, photo_zoom: g.photo_zoom ?? 1,
});

const withPhoto = async (form, file) => {
  if (!file) return { ...form };
  const m = await uploadMedia(file);
  return { ...form, photo_url: m.url };
};

export const useGuidesAdmin = (authFetch) => {
  const [guides, setGuides] = useState([]);
  const [guideInvites, setGuideInvites] = useState([]);
  const [inviteDays, setInviteDays] = useState(7);
  const [lastGuideLink, setLastGuideLink] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [gForm, setGForm] = useState(EMPTY_GUIDE);
  const [gFile, setGFile] = useState(null);
  const [gBusy, setGBusy] = useState(false);
  const [editingG, setEditingG] = useState(null);
  const [gEditForm, setGEditForm] = useState(EMPTY_GUIDE);
  const [gEditFile, setGEditFile] = useState(null);

  const inviteCall = async (path, opts, fallback) => {
    const res = await authFetch(path, opts);
    const data = await readJson(res);
    if (!res.ok) throw new Error(data.detail || fallback);
    return data;
  };

  const createGuideInvite = async () => {
    setInviteBusy(true);
    try {
      const data = await inviteCall(`/admin/guide-invites`, { method: "POST", body: JSON.stringify({ expires_days: inviteDays }) }, "Could not create guide link");
      const link = `${window.location.origin}${data.path}`;
      const { token, path, ...invite } = data;
      setGuideInvites((items) => [invite, ...items]);
      setLastGuideLink(link);
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Guide link created and copied");
      } catch {
        toast.success("Guide link created");
      }
    } catch (err) {
      toast.error(err.message || "Could not create guide link");
    } finally {
      setInviteBusy(false);
    }
  };

  const copyGuideLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Guide link copied");
    } catch {
      window.prompt("Copy guide link", link);
    }
  };

  const disableGuideInvite = async (invite) => {
    if (!window.confirm("Disable this guide submission link?")) return;
    try {
      const data = await inviteCall(`/admin/guide-invites/${invite.id}/disable`, { method: "PUT", body: JSON.stringify({}) }, "Could not disable link");
      setGuideInvites((items) => items.map((item) => (item.id === invite.id ? data : item)));
      toast.success("Guide link disabled");
    } catch (err) {
      toast.error(err.message || "Could not disable link");
    }
  };

  const deleteGuideInvite = async (invite) => {
    if (!window.confirm("Delete this used/disabled guide link from the list?")) return;
    try {
      await inviteCall(`/admin/guide-invites/${invite.id}`, { method: "DELETE" }, "Could not delete link");
      setGuideInvites((items) => items.filter((item) => item.id !== invite.id));
      toast.success("Guide link deleted");
    } catch (err) {
      toast.error(err.message || "Could not delete link");
    }
  };

  const approveGuide = async (g) => {
    try {
      const data = await inviteCall(`/admin/guides/${g.id}/approve`, { method: "PUT", body: JSON.stringify({}) }, "Could not approve guide");
      setGuides((gs) => gs.map((x) => (x.id === g.id ? data : x)));
      toast.success(`${g.name} approved`);
    } catch (err) {
      toast.error(err.message || "Could not approve guide");
    }
  };

  const addGuide = async (e) => {
    e.preventDefault();
    if (!gForm.name.trim()) { toast.error("Name is required"); return; }
    setGBusy(true);
    try {
      const data = await inviteCall(`/admin/guides`, { method: "POST", body: JSON.stringify(await withPhoto(gForm, gFile)) }, "Failed");
      setGuides((g) => [...g, data]);
      setGForm(EMPTY_GUIDE);
      setGFile(null);
      toast.success("Guide added");
    } catch (err) {
      toast.error(err.message || "Failed to add guide");
    } finally {
      setGBusy(false);
    }
  };

  const startEditG = (g) => { setEditingG(g); setGEditForm(guideForm(g)); setGEditFile(null); };

  const saveEditG = async (e) => {
    e.preventDefault();
    try {
      const res = await authFetch(`/admin/guides/${editingG.id}`, { method: "PUT", body: JSON.stringify(await withPhoto(gEditForm, gEditFile)) });
      if (!res.ok) throw new Error();
      const updated = await res.json();
      setGuides((gs) => gs.map((x) => (x.id === updated.id ? updated : x)));
      toast.success("Guide updated");
      setEditingG(null);
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteGuide = async (g) => {
    if (!window.confirm(`Delete guide ${g.name}?`)) return;
    try {
      const res = await authFetch(`/admin/guides/${g.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
      setGuides((gs) => gs.filter((x) => x.id !== g.id));
      toast.success("Guide deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return {
    guides, setGuides, guideInvites, setGuideInvites, inviteDays, setInviteDays, lastGuideLink, inviteBusy,
    createGuideInvite, copyGuideLink, disableGuideInvite, deleteGuideInvite, approveGuide,
    gForm, setGForm, gFile, setGFile, gBusy, addGuide,
    editingG, setEditingG, gEditForm, setGEditForm, gEditFile, setGEditFile, startEditG, saveEditG, deleteGuide,
  };
};
